import { useSite, usePageSeo } from "@/context/SiteContext";
import { useJsonLd } from "@/lib/jsonld";
import { resolveImageUrl } from "@/lib/api";
import { HeroSection } from "@/components/sections/HeroSection";
import { CategoriesSection } from "@/components/sections/CategoriesSection";
import { FeaturedSection } from "@/components/sections/FeaturedSection";
import { RecentlySoldSection } from "@/components/sections/RecentlySoldSection";
import { FinancingSection } from "@/components/sections/FinancingSection";
import { BenefitsSection } from "@/components/sections/BenefitsSection";
import { FaqSection } from "@/components/sections/FaqSection";
import { TestimonialsSection } from "@/components/sections/TestimonialsSection";
import { ContactCtaSection } from "@/components/sections/ContactCtaSection";

const SECTION_COMPONENTS = {
  hero: HeroSection,
  categories: CategoriesSection,
  featured: FeaturedSection,
  recently_sold: RecentlySoldSection,
  financing: FinancingSection,
  benefits: BenefitsSection,
  faq: FaqSection,
  testimonials: TestimonialsSection,
  contact_cta: ContactCtaSection,
};

export default function HomePage() {
  usePageSeo();
  const { content, loading, publicUrl } = useSite();
  const biz = content.business || {};
  useJsonLd(
    biz.business_name
      ? {
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          name: biz.business_name,
          ...(publicUrl && { url: publicUrl }),
          ...(biz.phone && { telephone: biz.phone }),
          ...(biz.email && { email: biz.email }),
          ...(biz.hours && { openingHours: biz.hours }),
          ...(biz.address && {
            address: {
              "@type": "PostalAddress",
              streetAddress: biz.address,
              ...(biz.city && { addressLocality: biz.city }),
              ...(biz.province && { addressRegion: biz.province }),
              ...(biz.postal_code && { postalCode: biz.postal_code }),
            },
          }),
          ...((biz.logo_url || content.theme?.logo_url) && {
            image: resolveImageUrl(biz.logo_url || content.theme.logo_url),
          }),
          ...(biz.social?.length && { sameAs: biz.social.map((s) => s.url).filter(Boolean) }),
        }
      : null
  );
  const sections = content.homepage?.sections || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="home-loading">
        <div className="min-h-[80vh] bg-zinc-200 animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      {sections
        .filter((s) => s.enabled !== false)
        .map((s) => {
          const Component = SECTION_COMPONENTS[s.type];
          if (!Component) return null;
          return <Component key={s.id} content={s.content || {}} />;
        })}
    </div>
  );
}
